import { useMemo, type ReactNode } from 'react'
import { BrowserRouter, Routes, Route } from 'react-router'
import {
  QueryClient,
  QueryClientProvider,
  useQueryClient,
} from '@tanstack/react-query'
import { Toaster } from 'sonner'

import { AddressLookupProvider } from '@/shared/components/ui/address-lookup-context'
import { createConsumerAddressLookupApi } from '@/consumer/lib/address-lookup'
import { registerConsumerQueryDefaults } from '@/consumer/lib/query-defaults'
import { useConsumerBranding } from '@/consumer/hooks/use-consumer-branding'
import { AddressAutofillProvider } from '@/shared/components/ui/address-autofill-context'
import LoginPage from './pages/login-page'
import VerifyPage from './pages/verify-page'
import WalletPage from './pages/wallet-page'
import ProductsPage from './pages/products-page'
import RegisterPage from './pages/register-page'
import ProductDetailPage from './pages/product-detail-page'
import SubmitClaimPage from './pages/submit-claim-page'
import ClaimsPage from './pages/claims-page'
import ClaimDetailPage from './pages/claim-detail-page'
import ConsumerMultiItemClaimPage from './pages/multi-item-claim-page'
import OnboardingPage from './pages/onboarding-page'
import AccountPage from './pages/account-page'
import LegalPage from './pages/legal-page'
import PublicClaimPage from './pages/public-claim-page'
import WelcomePage from './pages/welcome/welcome-page'
import LabelScanPage from './pages/label-scan-page'
import { NotFoundPage } from '@/shared/components/not-found-page'
import { ConsumerProtectedRoute } from './components/consumer-protected-route'
import { ConsumerFormattingProvider, ConsumerThemeProvider } from './components/tenant-branding'
import { RequireConsumerClaims } from './components/require-consumer-claims'

// In-memory stale-while-revalidate: a 30-min gcTime keeps each search term's
// result around long enough to repaint instantly on re-type / picker re-open,
// and `registerConsumerQueryDefaults` sets the SEARCH policy per key prefix. The
// consumer client deliberately has NO IndexedDB persister (unlike the admin
// app) - the public/per-visitor surface must not write data to disk.
const queryClient = new QueryClient({
  defaultOptions: { queries: { gcTime: 30 * 60_000 } },
})
registerConsumerQueryDefaults(queryClient)

/**
 * Builds the cached consumer address-lookup API bound to the app's QueryClient,
 * so autocomplete/resolve/postcode share the `address-*` cache entries instead
 * of re-hitting the server on every keystroke. Mounted once here and once on the
 * account page (which has its own provider subtree).
 */
function ConsumerAddressLookupProvider({ children }: { children: ReactNode }) {
  const client = useQueryClient()
  const api = useMemo(() => createConsumerAddressLookupApi(client), [client])
  return <AddressLookupProvider api={api}>{children}</AddressLookupProvider>
}

/**
 * Feeds the shared address postcode-autofill gate from the tenant's hidden
 * `postcode_autofill` flag, exposed on the consumer-branding payload. On by
 * default platform-wide; a tenant opts out with an explicit `false` (Teknor,
 * whose addresses were auto-editing). `undefined` (pre-tenant) stays on.
 */
function ConsumerAddressAutofillProvider({ children }: { children: ReactNode }) {
  const { data } = useConsumerBranding()
  const enabled = data?.feature_flags?.postcode_autofill !== false
  return <AddressAutofillProvider enabled={enabled}>{children}</AddressAutofillProvider>
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ConsumerThemeProvider>
      <ConsumerFormattingProvider>
      <ConsumerAddressAutofillProvider>
      <ConsumerAddressLookupProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/verify" element={<VerifyPage />} />
          {/* Subdomain-scoped (`<tenant>.domain/claim`) is canonical; the
              `:orgSlug` path is a generic-host fallback for legacy links. */}
          <Route path="/claim" element={<PublicClaimPage />} />
          <Route path="/claim/:orgSlug" element={<PublicClaimPage />} />
          <Route path="/welcome" element={<WelcomePage />} />
          <Route path="/welcome/:slug" element={<WelcomePage />} />
          <Route path="/q/:token" element={<LabelScanPage />} />
          {/* Legal pages are public so they can be read before/at onboarding. */}
          <Route path="/terms-and-conditions" element={<LegalPage kind="terms" />} />
          <Route path="/privacy-policy" element={<LegalPage kind="privacy" />} />
          <Route
            path="/onboarding"
            element={
              <ConsumerProtectedRoute>
                <OnboardingPage />
              </ConsumerProtectedRoute>
            }
          />
          <Route
            path="/"
            element={
              <ConsumerProtectedRoute>
                <WalletPage />
              </ConsumerProtectedRoute>
            }
          />
          <Route
            path="/account"
            element={
              <ConsumerProtectedRoute>
                <AccountPage />
              </ConsumerProtectedRoute>
            }
          />
          <Route
            path="/register"
            element={
              <ConsumerProtectedRoute>
                <RegisterPage />
              </ConsumerProtectedRoute>
            }
          />
          <Route
            path="/products"
            element={
              <ConsumerProtectedRoute>
                <ProductsPage />
              </ConsumerProtectedRoute>
            }
          />
          <Route
            path="/products/:uuid"
            element={
              <ConsumerProtectedRoute>
                <ProductDetailPage />
              </ConsumerProtectedRoute>
            }
          />
          <Route
            path="/products/:uuid/submit-claim"
            element={
              <ConsumerProtectedRoute>
                <RequireConsumerClaims>
                  <SubmitClaimPage />
                </RequireConsumerClaims>
              </ConsumerProtectedRoute>
            }
          />
          <Route
            path="/claims"
            element={
              <ConsumerProtectedRoute>
                <ClaimsPage />
              </ConsumerProtectedRoute>
            }
          />
          <Route
            path="/claims/new"
            element={
              <ConsumerProtectedRoute>
                <RequireConsumerClaims>
                  <ConsumerMultiItemClaimPage />
                </RequireConsumerClaims>
              </ConsumerProtectedRoute>
            }
          />
          <Route
            path="/claims/:uuid"
            element={
              <ConsumerProtectedRoute>
                <ClaimDetailPage />
              </ConsumerProtectedRoute>
            }
          />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </BrowserRouter>
      </ConsumerAddressLookupProvider>
      </ConsumerAddressAutofillProvider>
      </ConsumerFormattingProvider>
      </ConsumerThemeProvider>
      <Toaster position="top-center" />
    </QueryClientProvider>
  )
}
